import {
	AGG_GOAL_DIRECTORY_ARCHIVED_PAGE,
	AGG_GOAL_DIRECTORY_FOLLOWING_PAGE,
} from '@townsquare/config/routes';
import { filterUndefined } from '@townsquare/filter-type';
import { tqlQueryFromInput } from '@townsquare/tql/query';
import { ComparatorOperator } from '@townsquare/tql/types';

import {
	DEFAULT_GOAL_SORTS,
	GOAL_DIRECTORY_SORTS_AGG,
	type GoalDirectorySortAgg,
} from './default-constants';

/**
 * Takes a raw sort key from the URL and transforms it to a new sort key.
 * e.g.
 * `NAME-ASC` -> `[NAME_ASC]`
 * `SCORE-ASC,NAME-ASC` -> `['SCORE_ASC', 'NAME_ASC']`
 */
export function resolveRawSort(rawSort?: string): GoalDirectorySortAgg[] | undefined {
	if (!rawSort) {
		return;
	}

	if (rawSort.includes(',')) {
		const sorts = rawSort.split(',').map(transformSortKey).filter(filterUndefined);
		return sorts.length > 0 ? sorts : undefined;
	} else {
		return resolveSorts(transformSortKey(rawSort));
	}
}

export function resolveSorts(primarySort?: GoalDirectorySortAgg): GoalDirectorySortAgg[] {
	const initialSorts: GoalDirectorySortAgg[] = [primarySort ?? DEFAULT_GOAL_SORTS[0]];
	const sorts = new Set<GoalDirectorySortAgg>(initialSorts);
	return Array.from(sorts.add('NAME_ASC'));
}

const isGoalDirectorySortAgg = (sort: string): sort is GoalDirectorySortAgg => {
	return GOAL_DIRECTORY_SORTS_AGG.includes(sort as GoalDirectorySortAgg);
};

export function transformSortKey(key?: string): GoalDirectorySortAgg | undefined {
	const [sortKey, sortOrder] = key?.split('-') ?? [];
	const transformedSortKey = sortKey && sortOrder ? `${sortKey}_${sortOrder}` : key;

	return transformedSortKey && isGoalDirectorySortAgg(transformedSortKey)
		? transformedSortKey
		: undefined;
}

const getArchivedTqlClause = (archived: boolean) => ({
	fieldName: 'archived',
	fieldValue: archived,
	comparator: ComparatorOperator.EQUALS,
});

const getFollowingTqlClause = () => ({
	fieldName: 'following',
	fieldValue: true,
	comparator: ComparatorOperator.EQUALS,
});

export const getDefaultGoalTqlQuery = (): ReturnType<typeof tqlQueryFromInput> =>
	tqlQueryFromInput({
		input: [getArchivedTqlClause(false)],
	});

export const getDefaultGoalTqlQueryForRoute = (
	routeName?: string,
): ReturnType<typeof tqlQueryFromInput> => {
	if (routeName === AGG_GOAL_DIRECTORY_ARCHIVED_PAGE) {
		return tqlQueryFromInput({
			input: [getArchivedTqlClause(true)],
		});
	}

	if (routeName === AGG_GOAL_DIRECTORY_FOLLOWING_PAGE) {
		return tqlQueryFromInput({
			input: [getArchivedTqlClause(false), getFollowingTqlClause()],
		});
	}

	return getDefaultGoalTqlQuery();
};
